import { Injectable } from '@angular/core';
import { ApiClientService } from '../api-client.service';
import { Observable } from 'rxjs/Observable';
import { IPropertyManagerRole } from '../../types/api-response/auth/roles';
import { catchError, map } from 'rxjs/operators';
import { IMe } from '../../types/api-response/auth/me';
import { IAuthUser } from '../../types/api-response/auth/auth-user';
import { ApiResponseList } from '../../types/api-response/helpers';
import { _throw } from 'rxjs/observable/throw';
import { IWebsiteBuilder } from '../../types/api-response/auth/website-builder';

@Injectable()
export class AuthApiService {

    constructor(private api: ApiClientService) {}

    /**
     * Returns roles list of current user
     *
     */
    getMyRoles(user: Observable<IMe>): Observable<IPropertyManagerRole[]> {
        return user.pipe(
          map(response => response.buildingPropertyManager.map(building => building.role)
              .filter(role => role) // filters empty roles from array
              .filter(onlyUniqueRole)
          )
        );

        // filters unique roles from array
        function onlyUniqueRole(value: IPropertyManagerRole, index: number, self: IPropertyManagerRole[]) {
            return self.findIndex(role => role.id === value.id) === index;
        }
    }

    /**
     * Returns users list
     *
     * @param params
     */
    getUsers(params: {[key: string]: string|number|Array<string|number>} = {}): Observable<ApiResponseList<IAuthUser>> {
        const url = '/api/users.json' + ApiClientService.serialize(params);

        return this.api.getJson('oauthUrl', url) as any;
    }

    /**
     * Returns profile details
     *
     * @param params
     */
    getMyProfile(params: {[key: string]: string|number} = {}): Observable<IMe> {
        const url = '/api/users/me.json' + ApiClientService.serialize(params);

        return this.api.getJson('oauthUrl', url) as any;
    }

    /**
     * Returns active service keys
     * for selected building ids
     *
     * @param buildingId
     */
    getBuildingServices(buildingId: number | number[]) {
        const url = '/api/v1/building_service.json' + ApiClientService.serialize({buildingId});

        return this.api.getJson('oauthUrl', url);
    }

    /**
     * Sets default dashboard for selected user ID
     *
     * @param userId
     * @param dashboardId
     */
    setDefaultUserDashboard(userId, dashboardId: string|number) {
        const url = `/api/v1/users/${userId}.json`;

        return this.api.patch('oauthUrl', url, {defaultDashboardId: dashboardId});
    }

    /**
     * Returns website builder list
     * for selected building ids
     *
     * @param params
     */
    getWebsiteBuilderList(params = {}): Observable<ApiResponseList<IWebsiteBuilder>> {
        const allowedParams = ['page', 'per-page', 'expand', 'corporation_number_id', 'status', 'announcements_status', 'query', 'order-by'];
        const url = '/api/v1/website_builder.json' + ApiClientService.serialize(params, allowedParams);

        return this.api.getJson('oauthUrl', url);
    }

    /**
     * Set website
     *
     * @param data
     */
    setWebsiteBuilder(data): Observable<IWebsiteBuilder> {
        return data.id ? this.updateWebsiteBuilder(data) : this.createWebsiteBuilder(data);
    }

    /**
     * Creates new website builder
     *
     * @param data
     */
    createWebsiteBuilder(data): Observable<IWebsiteBuilder> {
        return this.api.post('oauthUrl', '/api/v1/website_builder.json', data).pipe(
            catchError(error => {
                this.api.errorHandler(error, 'Failed to create Website');

                return _throw(error);
            })
        );
    }

    /**
     * Update existing website
     *
     * @param data
     */
    updateWebsiteBuilder(data): Observable<IWebsiteBuilder> {
        const postData: IWebsiteBuilder = {...data};
        delete postData.id;

        return this.api.patch('oauthUrl', `/api/v1/website_builder/${data.id}.json`, postData).pipe(
            catchError(error => {
                this.api.errorHandler(error, 'Failed to update Website Data');

                return _throw(error);
            })
        );
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/api/auth-api.service.ts